import CourseCard from './CardCourse';
import { IShowcaseCourse } from 'interface/showcase';
import Carousel from '../../Carousel';
import { useState, useEffect, useCallback, useRef } from 'react';

import showcaseService, { TFilters } from 'services/showcase';

interface IProps {
  title: string;
  categoryId: number;
  shelfId: number;
}

const CoursesShelf = (props: Partial<IProps>) => {
  const [courses, setCourses] = useState<IShowcaseCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const filterRef = useRef<TFilters>({ page: 1, total_pages: 1 });
  const notLoadedRef = useRef(true);
  const shelfRef = useRef<HTMLDivElement>(null);

  const fetchCourses = useCallback(
    (params: TFilters = {}) => {
      setLoading(true);
      return showcaseService
        .getCourses<IShowcaseCourse[]>({ ...params, categoryId: props.categoryId, shelveId: props.shelfId })
        .then(({ filters, data }) => {
          filterRef.current = filters;
          setCourses((currentCourses) => [...currentCourses, ...data]);
        })
        .finally(() => setLoading(false));
    },
    [props.categoryId, props.shelfId]
  );

  const fetchMore = () => {
    if (loading) return;
    const nextPage = filterRef.current.page + 1;
    if (nextPage > filterRef.current.total_pages) return;
    fetchCourses({ ...filterRef.current, page: nextPage });
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout = null;
    if (!notLoadedRef.current) {
      window.removeEventListener('scroll', () => {});
      clearTimeout(timeout);
    }
    const onScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        clearTimeout(timeout);
        if (!shelfRef.current) return;
        const { top, bottom } = shelfRef.current.getBoundingClientRect();
        const isOnScreen = top >= 0 && bottom <= window.innerHeight + 400;
        if (isOnScreen && notLoadedRef.current) {
          notLoadedRef.current = false;
          fetchCourses();
        }
      }, 200);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, false);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [courses.length, fetchCourses]);

  const { page, total_pages } = filterRef.current;

  return (
    <div ref={shelfRef}>
      <Carousel
        id='showcase-shelf-courses'
        title={props.title}
        isEmpty={(courses ?? []).length === 0}
        loadingShelf={loading && courses.length === 0}
        fetchingContent={loading}
        fetchMore={fetchMore}
        hasMoreContentToFetch={page !== total_pages}
      >
        {(courses ?? []).map((course) => (
          <CourseCard key={course.id} course={course as any as IShowcaseCourse} />
        ))}
      </Carousel>
    </div>
  );
};

export default CoursesShelf;
